import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { HashRouter } from 'react-router-dom';

import { Paper, Tab, Tabs } from '@mui/material';

import StyledTabs from 'components/shared/StyledTabs';

import DataGridToolBar from './Headers/HeaderGrid/DataGridToolBar';
import { PageType, tableContext } from './Headers/HeaderGrid/useTableContext';
import useTabNavigation from './hooks/useTabNavigation';
import TabsRouter from './router/TabsRouter';
import { useNavParams, useSelector } from '../../../hooks';
import { useSessionStorage } from 'usehooks-ts';
import { ADMIN_SESSION_STORAGE_KEYS } from './types';

export type TabType = {
  id: number;
  name: string;
  Component: React.FC<any>;
  parent: any;
  requiredGrants: string[];
};

type TabView = {
  name: string;
  parent: string;
  tabIndex: number;
};

export interface Header {
  subHeaderName: string;
  tabs: TabType[];
  requiredGrants?: string[];
}

interface Props {
  page: string;
  headers: Header[];
  views: TabType[];
  userGrantNeeded: string;
}

const BaseTabsRouting: React.FC<Props> = ({
  page,
  headers,
  views,
  userGrantNeeded
}) => {
  const { t } = useTranslation('common');
  const [pageType, setPageType] = useState<PageType>(PageType.LIST);
  const { setParams } = useNavParams();
  const [currentView, setCurrentView] = useSessionStorage<null | TabView>(
    ADMIN_SESSION_STORAGE_KEYS.LAST_LOCATION,
    null
  );
  const tabbedElementRef = useRef<null | TabView>(null); // to store the current tabbed element
  const portalTargetRef = useRef<HTMLDivElement>(null);
  const grants = useSelector((state) => state.grants);

  //filter and sort by name
  const filteredViews = views
    .filter(
      (view) =>
        !view.requiredGrants ||
        view.requiredGrants.every((requiredGrant) => grants[requiredGrant])
    )
    .sort((a, b) =>
      t(`tableTabs.${a.name}`).localeCompare(t(`tableTabs.${b.name}`))
    );

  const filteredHeaders = headers
    .filter(
      (header) =>
        !header.requiredGrants ||
        header.requiredGrants.every((requiredGrant) => {
          return grants[requiredGrant];
        })
    )
    .map((header) => ({
      ...header,
      tabs: header.tabs.sort((a, b) =>
        t(`tableTabs.${a.name}`).localeCompare(t(`tableTabs.${b.name}`))
      )
    }));

  const { tabIndex, setTabIndex, tabbedElement, setTabbedElement } =
    useTabNavigation(
      0,
      filteredHeaders[0].tabs[0],
      filteredHeaders,
      filteredViews
    );

  const set = useCallback(
    (currentView: null | TabView) => setCurrentView(currentView),
    [tabbedElement]
  );

  // setting currentView on hard reload
  useEffect(() => {
    window.addEventListener('beforeunload', () =>
      set(tabbedElementRef.current)
    );
    tabbedElementRef.current = {
      name: tabbedElement.name,
      parent: tabbedElement.parent,
      tabIndex: tabIndex
    };
  }, [tabbedElement]);

  // setting currentView at unmount
  useEffect(() => {
    return () => {
      window.removeEventListener('beforeunload', () =>
        set(tabbedElementRef.current)
      );
      set(tabbedElementRef.current);
    };
  }, []);

  // set the page param on initial load
  useEffect(() => {
    if (currentView) {
      const tab = filteredHeaders[currentView.tabIndex].tabs.find(
        (tab) => tab.name === currentView.name
      );

      if (tab) {
        setParams({
          page: tab.name.toLowerCase()
        });
      }
    }
  }, []);

  const handleChange = (e) => {
    setTabIndex(e.target.tabIndex);
    setTabbedElement(filteredHeaders[e.target.tabIndex].tabs[0]);
  };

  return (
    <StyledTabs className="tabs">
      <Paper square>
        <Tabs
          variant="scrollable"
          allowScrollButtonsMobile
          value={tabIndex}
          indicatorColor="primary"
          textColor="primary"
          onChange={handleChange}
        >
          {filteredHeaders
            .filter(
              ({ requiredGrants }) =>
                !requiredGrants ||
                requiredGrants.every((grant: string) => grants[grant])
            )
            .map((header, index) => (
              <Tab
                key={index}
                component="a"
                tabIndex={index}
                label={t(`tableHeaders.${header.subHeaderName}`)}
                href={`/#${page}&page=${header.tabs[0].name.toLowerCase()}`}
                onClick={() => setPageType(PageType.LIST)}
              />
            ))}
        </Tabs>
      </Paper>
      <div style={{ height: '100%', padding: 30 }}>
        <HashRouter hashType="noslash" baseName={'/'}>
          <tableContext.Provider
            value={{
              contentNodeForButtons: portalTargetRef,
              context: {
                page: page,
                type: pageType,
                setPageType: setPageType
              }
            }}
          >
            <DataGridToolBar
              setTabbedElement={setTabbedElement}
              tabbedElement={tabbedElement}
              tabulatedItems={filteredHeaders[tabIndex].tabs.filter(
                (tab) =>
                  !tab.requiredGrants ||
                  tab.requiredGrants.every((grant) => grants[grant])
              )}
              page={page}
            />
            <TabsRouter
              page={page}
              views={filteredViews}
              requiredGrants={userGrantNeeded}
            />
          </tableContext.Provider>
        </HashRouter>
      </div>
    </StyledTabs>
  );
};

export default BaseTabsRouting;
